<template>
  <section>
    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>
      <transaction
        v-if="isTransactionModalVisible"
        informationMessage="Transactions older than 1 Jan 2013 are not shown">
      </transaction>
      <information v-if="isInformationModalVisible"></information>
      <div class="content-heading">
        Manage Investments
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
             <dismissable-alert
                title="You've purchased a Community Investment Note!"
                subtitle="Your investment will take 3 to 4 business days to be processed. You will receive a notification when it's completed."
                :closeAction="hidePurchaseCompletedAlert"
                :isShowing="showPurchaseCompleted"
              />
              <button v-if="!disableInvestment && (userStatus && userStatus.status !== 'no_status') && !isInstitutionAccount" type="button" v-on:click="purchaseNote()" class="btn btn-default btn-lg" :disabled="isReadOnly">
                    Make an investment
              </button>
              <div v-if="userStatus && userStatus.status !== 'new'">
                <div class="current-balance">
                  <p class="msg text-muted">Current account balance: <strong class="amountBalance">{{ currentAmountBalance }}</strong></p>
                </div>
              </div>
            <div class="panel-heading">
              <div class="panel-title">
                <span class="small-field">Notes list</span>
              </div>
            </div>

            <!-- Desktop Table View -->
            <div class="table-responsive top-margin desktop-table">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th class="text-center">
                      <div @click="sortByNoteId()" class="note-sort-title small-container">
                        <span class="small-field">Note number</span>
                        <em :class="sortIconStyle('noteId')"></em>
                      </div>
                    </th>
                    <th class="text-right">
                      <div @click="sortNotes('currentBalance')" class="note-sort-title large-container">
                        <div class="field-container">
                          <span>Current</span>
                          <span>principal</span>
                          <span>amount</span>
                        </div>
                        <em :class="sortIconStyle('currentBalance')"></em>
                      </div>
                    </th>
                    <th class="text-right">
                      <div @click="sortNotes('originalBalance')" class="note-sort-title large-container">
                        <div class="field-container">
                          <span>
                            Original
                          </span>
                          <span>
                            principal
                          </span>
                          <span>
                            amount
                          </span>
                        </div>
                        <em :class="sortIconStyle('originalBalance')"></em>
                      </div>
                    </th>
                    <th class="text-center">
                      <div @click="sortNotes('interestRate')" class="note-sort-title small-container">
                        <span class="small-field">Rate</span>
                        <em :class="sortIconStyle('interestRate')"></em>
                      </div>
                    </th>
                    <th class="text-center">
                      <div @click="sortNotes('interestTerm')" class="note-sort-title small-container">
                        <span class="small-field">Term</span>
                        <em :class="sortIconStyle('interestTerm')"></em>
                      </div>
                    </th>
                    <th class="text-center">
                      <div @click="sortNotes('purchasedOn')" class="note-sort-title small-container">
                        <div class="field-container">
                          <span class="small-field">Purchase date</span>
                        </div>
                        <em :class="sortIconStyle('purchasedOn')"></em>
                      </div>
                    </th>
                    <th class="text-center">
                      <div @click="sortNotes('openedOn')" class="note-sort-title small-container">
                        <span class="small-field">Open date</span>
                        <em :class="sortIconStyle('openedOn')"></em>
                      </div>
                    </th>
                    <th class="text-center">
                      <div @click="sortNotes('maturityDate')" class="note-sort-title small-container">
                        <span class="small-field">Maturity date</span>
                        <em :class="sortIconStyle('maturityDate')"></em>
                      </div>
                    </th>
                    <th class="text-center">
                      <div @click="sortByStatus()" class="note-sort-title small-container">
                        <span class="small-field">Status</span>
                        <em :class="sortIconStyle('status')"></em>
                      </div>
                    </th>
                    <th class="text-center">View details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="note in orderedNotes" :key="note.noteId">
                    <td class="text-center">{{note.noteId}}</td>
                    <td class="text-right">{{formatAmount(note.currentBalance)}}</td>
                    <td class="text-right">{{formatAmount(note.originalBalance)}}</td>
                    <td class="text-center">{{formatRate(note.interestRate)}}</td>
                    <td class="text-center">{{formatTerm(note.interestTerm)}}</td>
                    <td class="text-center">{{formatDate(note.purchasedOn)}}</td>
                    <td class="text-center">{{formatDate(note.openedOn)}}</td>
                    <td class="text-center">{{formatDate(note.maturityDate)}}</td>
                    <td class="text-center">
                      <span class="label label-danger" v-if="note.status == 'cancelled'">Cancelled</span>
                      <span class="label label-danger" v-if="note.status == 'failed'">Failed</span>
                      <span class="label label-warning" v-if="note.status == 'pending'">Pending</span>
                      <span class="label label-success" v-if="note.status == 'open'">Open</span>
                      <span class="label label-danger" v-if="note.status == 'closed'">Closed</span>
                    </td>
                    <td class="text-center">
                      <button type="button" class="btn btn-details btn-outline" :disabled="isReadOnly" v-on:click="goToViewDetail(note)">View details</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Card View -->
            <div class="mobile-card-view">
              <div class="notes-cards-mobile">
                <div
                  v-for="note in orderedNotes"
                  :key="note.noteId"
                  class="note-card-mobile"
                >
                  <div class="note-card-mobile-header">
                    <div class="note-card-mobile-number">
                      <span class="note-label">Note</span>
                      <span class="note-number">{{note.noteId}}</span>
                    </div>
                    <span class="label note-status-badge"
                      :class="{
                        'label-danger': note.status === 'cancelled' || note.status === 'failed' || note.status === 'closed',
                        'label-warning': note.status === 'pending',
                        'label-success': note.status === 'open'
                      }">
                      {{note.status}}
                    </span>
                  </div>

                  <div class="note-card-mobile-body">
                    <div class="note-card-mobile-row highlight-row">
                      <span class="note-card-mobile-label">Current principal:</span>
                      <span class="note-card-mobile-value amount-value">{{formatAmount(note.currentBalance)}}</span>
                    </div>

                    <div class="note-card-mobile-row">
                      <span class="note-card-mobile-label">Original principal:</span>
                      <span class="note-card-mobile-value">{{formatAmount(note.originalBalance)}}</span>
                    </div>

                    <div class="note-card-mobile-row">
                      <span class="note-card-mobile-label">Rate:</span>
                      <span class="note-card-mobile-value">{{formatRate(note.interestRate)}}</span>
                    </div>

                    <div class="note-card-mobile-row">
                      <span class="note-card-mobile-label">Term:</span>
                      <span class="note-card-mobile-value">{{formatTerm(note.interestTerm)}}</span>
                    </div>

                    <div class="note-card-mobile-row">
                      <span class="note-card-mobile-label">Purchase date:</span>
                      <span class="note-card-mobile-value">{{formatDate(note.purchasedOn)}}</span>
                    </div>

                    <div class="note-card-mobile-row">
                      <span class="note-card-mobile-label">Open date:</span>
                      <span class="note-card-mobile-value">{{formatDate(note.openedOn)}}</span>
                    </div>

                    <div class="note-card-mobile-row">
                      <span class="note-card-mobile-label">Maturity date:</span>
                      <span class="note-card-mobile-value">{{formatDate(note.maturityDate)}}</span>
                    </div>
                  </div>

                  <div class="note-card-mobile-action">
                    <button
                      type="button"
                      class="btn btn-mobile-details"
                      :disabled="isReadOnly"
                      @click="goToViewDetail(note)">
                      <i class="fa fa-eye"></i> View details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapGetters,
    mapMutations,
    mapState,
  } from 'vuex';

  import _ from 'lodash';
  import Errors from '@/components/modals/Errors';
  import spinner from '@/components/widgets/Spinner';
  import DismissableAlert from '@/components/widgets/DismissableAlert';
  import transaction from '@/components/modals/Transaction';
  import information from '@/components/modals/Information';

  import TransactionTypes from '@/data/static/transactionTypes';
  import TransactionStatuses from '@/data/static/transactionStatuses';

  import {
    formatDate,
    formatAmount,
    formatRate,
    formatTerm,
    isStateRestricted,
    orderTransactions,
    } from '@/util';

  import {
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
    SHOW_TRANSACTION_MODAL,
    SHOW_INFORMATION_MODAL,
  } from '@/store/mutation-types';


  const mix = {
    methods: {
      formatDate,
      formatAmount,
      formatRate,
      formatTerm,
      isStateRestricted,
    },
  };
  export default {
    name: 'notesList',
    async beforeMount() {
      this.showPurchaseCompleted = this.$route.params.purchasedNote;
      this.loadNotesList();
      this.checkUserStatus();
      await this.getAmountBalance();
    },
    mixins: [mix],
    components: {
      errors: Errors,
      'dismissable-alert': DismissableAlert,
      spinner,
      transaction,
      information,
    },
    data() {
      return {
        notes: [],
        notesError: '',
        loading: false,
        orderedNotes: [],
        sortProperty: '',
        sortToggle: 1,
        showPurchaseCompleted: false,
        currentAmountBalance: 0,
      };
    },
    computed: {
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('home', [
        'userStatus',
      ]),
      ...mapState('transactionModal', [
        'isTransactionModalVisible',
      ]),
      ...mapState('app', [
        'activeAccountId',
        'user',
        'activeAccount',
        'accountTypes',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
      ]),
      isInstitutionAccount() {
        return this.activeAccount.accountType === this.accountTypes.INSTITUTION.name;
      },
      disableInvestment() {
        const { accounts, disableInvestment } = this.user;
        const account = accounts.find(acct => acct.id === this.activeAccount.id);
        return disableInvestment || account.disableInvestment;
      },
      isReadOnly() {
        return this.user && this.user.userType === 'READ_ONLY';
      },
    },
    methods: {
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      ...mapActions('home', [
        'getUserStatus',
      ]),
      ...mapActions('account', [
        'getCurrentAmountBalance',
        'getAccountNotes',
        'getAccountNoteTransactions',
      ]),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('transactionModal', {
        showTransactionModal: SHOW_TRANSACTION_MODAL,
      }),
      async getAmountBalance() {
        const response = await this.getCurrentAmountBalance({ accountId: this.activeAccountId });
        this.currentAmountBalance = formatAmount(response.currentAmountBalance);
      },
      async loadNotesList() {
        return this.getAccountNotes(this.activeAccountId)
          .then((notes) => {
            this.notes = notes;
            this.sortByStatus();
          })
          .catch(() => {
            this.setErrorsModalInfo(['We could not retrieve notes list']);
            this.showErrorsModal();
          });
      },
      purchaseNote() {
        if (this.isReadOnly) return;
        const state = this.user.residentialAddress.state;
        if (this.isStateRestricted(state)) {
          const restrictedMessage = `At this time, we cannot accept investments from persons resident in ${state} state.
            Please contact Calvert Impact Capital at info@calvertimpact.org or call us at 800-248-0337 if you have questions. `;
          this.showInformationModal(restrictedMessage);
        } else if (
          this.userStatus.needs.includes('FUNDING_SOURCE') ||
          this.userStatus.status === 'document_migrated_user' ||
          this.userStatus.status === 'retry'
          ) {
          this.$router.push({ name: 'AddFundingSource' });
        } else {
          this.$router.push({ name: 'PurchaseNote' });
        }
      },
      async checkUserStatus() {
        this.loading = true;

        if (!this.hasBeenCreatedAccount) {
          return this.$router.push({ name: 'AccountSummary' });
        }

        return this.getUserStatus({
          accountId: this.activeAccount.id,
        }).then(({ status, needs }) => {
          if (status === 'retry') {
            this.$router.push({ name: 'AccountCreation' });
          } else if (status === 'document') {
            this.$router.push({ name: 'DocumentUpload' });
          } else if (status === 'suspended') {
            this.$router.push({ name: 'AccountSuspended' });
          } else if (!needs.includes('FUNDING_SOURCE') && needs.includes('NOTE')) {
            this.$router.push({ name: 'PurchaseNote', query: { first: 1 } });
          } else {
            this.loading = false;
          }
        }).catch(() => {
          this.loading = false;
        });
      },
      isTransactionAProcessedUpcomingPayment({ type, status }) {
        return (type === TransactionTypes.INT_PAID
            || type === TransactionTypes.SELL_MATURE
            || type === TransactionTypes.INT_REINVEST
            || type === TransactionTypes.SELL_REINVEST
            || type === TransactionTypes.INT_DONATE
            || type === TransactionTypes.SELL_DONATE)
            && (status === TransactionStatuses.PROCESSED
              || status === TransactionStatuses.PREPENDING);
      },
      isTransactionACancelledUpcomingPayment({ type, status }) {
        return (type === TransactionTypes.INT_PAID
            || type === TransactionTypes.SELL_MATURE
            || type === TransactionTypes.INT_REINVEST
            || type === TransactionTypes.SELL_REINVEST
            || type === TransactionTypes.INT_DONATE
            || type === TransactionTypes.SELL_DONATE)
            && (status === TransactionStatuses.CANCELLED
              || status === TransactionStatuses.FAILED);
      },
      isTransactionAFundingAccount({ type, dwollaTransferId }) {
        return (type === TransactionTypes.FUNDING
          && dwollaTransferId === null);
      },
      filterTransaction(transactionObject) {
        const { retry, type, status, dwollaTransferId } = transactionObject;
        if (status === TransactionStatuses.PREPENDING) {
          return false;
        } else if (retry) {
          return false;
        } else if (this.isTransactionAFundingAccount({ type, dwollaTransferId })) {
          return true;
        } else if (this.isTransactionAProcessedUpcomingPayment({ type, status })) {
          return true;
        } else if (type === TransactionTypes.PURCHASE_NOTE
          || type === TransactionTypes.ADDITIONAL_DEPOSIT
          || type === TransactionTypes.PURCH_INT_REINVEST
          || type === TransactionTypes.PURCH_REINVEST
          || type === TransactionTypes.REPAID_ACCT
          || type === TransactionTypes.PAID_INT
          || type === TransactionTypes.REPAID_ACCT_FULL
          || type === TransactionTypes.FEE_SERV
          || type === TransactionTypes.REPAID_ACCT_PARTIAL
          || type === TransactionTypes.PURCH_RENEWAL
          || type === TransactionTypes.ACCT_TRANSFER
          || type === TransactionTypes.PURCH_INT_CAP
          || type === TransactionTypes.SETTLEMENT) {
          return true;
        }
        return false;
      },
      cleanTransactionBasedOnCashoutProcess(transactions) {
        const isThereAnyRepaidAcct = _.some(transactions, { type: TransactionTypes.REPAID_ACCT });
        const isThereAnyPaidInt = _.some(transactions, { type: TransactionTypes.PAID_INT });

        if (!isThereAnyRepaidAcct && !isThereAnyPaidInt) {
          return transactions;
        }

        let cleanedTransactions = transactions;

        if (isThereAnyRepaidAcct) {
          cleanedTransactions = this.cleanTransactionsFromSellMature(transactions);
        }

        if (isThereAnyPaidInt) {
          cleanedTransactions = this.cleanTransactionsFromIntPaid(cleanedTransactions);
        }


        return cleanedTransactions;
      },
      cleanTransactionsFromCancelledUpcoming(transactions) {
        const cleanedTransactions = [];
        _.forEach(transactions, (t) => {
          if (!this.isTransactionACancelledUpcomingPayment(t)) {
            cleanedTransactions.push(t);
          }
        });
        return cleanedTransactions;
      },
      cleanTransactionsFromIntPaid(transactions) {
        const intPaidTransactions = _.filter(transactions, { type: TransactionTypes.INT_PAID });

        _.forEach(intPaidTransactions, (intPaid) => {
          const paidIntTransaction = _.find(transactions, {
            type: TransactionTypes.PAID_INT,
            createdAt: intPaid.maturityDate,
          });

          if (paidIntTransaction) {
            _.remove(transactions, { id: intPaid.id });
          }
        });

        return transactions;
      },
      cleanTransactionsFromSellMature(transactions) {
        const sellMatureTransactions = _.filter(transactions, { type: TransactionTypes.SELL_MATURE });

        _.forEach(sellMatureTransactions, (sellMature) => {
          const repaidAccTransaction = _.find(transactions, {
            type: TransactionTypes.REPAID_ACCT,
            createdAt: sellMature.maturityDate,
          });

          if (repaidAccTransaction) {
            _.remove(transactions, { id: sellMature.id });
          }
        });

        return transactions;
      },
      async goToViewDetail(note) {
        const transactions = await this.getAccountNoteTransactions({
          noteId: note.id,
          accountId: this.activeAccount.id,
        });
        const transactionsFiltered = transactions.filter(transactionObject =>
          this.filterTransaction(transactionObject));

        let cleanedTransactions = this.cleanTransactionBasedOnCashoutProcess(transactionsFiltered);
        cleanedTransactions = this.cleanTransactionsFromCancelledUpcoming(cleanedTransactions);

        const orderedTransactions = orderTransactions(cleanedTransactions);

        if (this.isReadOnly) return;
        const noteObject = {
          id: note.noteId,
          transactions: orderedTransactions,
          openedOn: note.openedOn,
        };
        this.showTransactionModal(noteObject);
      },
      sortByNoteId() {
        this.sortToggle = this.sortProperty === 'noteId' ? this.sortToggle * -1 : 1;

        this.orderedNotes = this.notes.sort((a, b) => {
          if (+a.noteId.match(/\d+/g) < +b.noteId.match(/\d+/g)) {
            return -1 * this.sortToggle;
          }
          if (+a.noteId.match(/\d+/g) > +b.noteId.match(/\d+/g)) {
            return 1 * this.sortToggle;
          }
          return 0;
        });

        this.sortProperty = 'noteId';
      },
      sortByStatus() {
        this.sortToggle = this.sortProperty === 'status' ? this.sortToggle * -1 : -1;

        this.orderedNotes = this.notes.sort((a, b) => {
          if (a.status < b.status || (!a.status && b.status)) {
            return -1 * this.sortToggle;
          }
          if (a.status > b.status || (a.status && !b.status)) {
            return 1 * this.sortToggle;
          }
          if (a.purchasedOn < b.purchasedOn || (!a.purchasedOn && b.purchasedOn)) {
            return 1;
          }
          if (a.purchasedOn > b.purchasedOn || (a.purchasedOn && !b.purchasedOn)) {
            return -1;
          }
          return 0;
        });

        this.sortProperty = 'status';
      },
      sortNotes(property) {
        this.sortToggle = this.sortProperty === property ? this.sortToggle * -1 : 1;

        this.orderedNotes = this.notes.sort((a, b) => {
          if (a[property] < b[property] || (!a[property] && b[property])) {
            return -1 * this.sortToggle;
          }
          if (a[property] > b[property] || (a[property] && !b[property])) {
            return 1 * this.sortToggle;
          }
          return 0;
        });

        this.sortProperty = property;
      },
      sortIconStyle(property) {
        if (property === this.sortProperty) {
          return `fa fa-sort-${this.sortToggle === 1 ? 'asc' : 'desc'}`;
        }
        return 'fa fa-sort';
      },
      hidePurchaseCompletedAlert() {
        this.showPurchaseCompleted = false;
      },
    },
  };
</script>

<style scoped>

  .notes-list-title {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .content-heading {
    font-weight: normal;
  }

  .panel-default {
    padding: 20px 30px;
  }

  .panel-heading {
    color: #444;
    background-color: #ffffff;
    border-color: #cfdbe2;
    border-bottom: 0;
    font-size: 14px;
    padding: 0px 5px;
  }

  table {
    border-collapse: collapse;
  }

  th {
    padding: 14px 8px;
    color: #888;
    border-bottom-width: 1px;
    vertical-align: bottom;
    border-bottom: 1px solid #eee;
    border-top: 0;
    line-height: 1.52857143;
    font-weight: normal;
    user-select: none;
  }

  td {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }

  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }

  .note-sort-title {
    display: flex;
    align-items: center;
  }

  .field-container {
    display: flex;
    flex-direction: column;
  }

  .note-sort-title:hover {
    cursor: pointer;
  }

  .fa {
    padding-left: 10px;
    margin-top: auto;
    margin-bottom: 3px;
  }

  .small-container {
    justify-content: center;
  }

  .large-container {
    justify-content: flex-end;
  }

   .amountBalance {
    font-size: 17px;
    color: #000000;
  }

  .current-balance {
    margin-top: 10px;
  }

  /* Mobile card view - hidden on desktop */
  .mobile-card-view {
    display: none;
  }

  /* Desktop table view - visible by default */
  .desktop-table {
    display: block;
  }

  /* Mobile responsive styles */
  @media (max-width: 1024px) {
    /* Hide desktop table, show mobile cards */
    .desktop-table {
      display: none !important;
    }

    .mobile-card-view {
      display: block;
      padding: 0 20px;
      margin-top: 20px;
    }

    .content-heading {
      font-size: 22px !important;
      padding: 20px 20px 16px 20px !important;
    }

    .panel-default {
      padding: 20px 0 !important;
      margin: 0 !important;
    }

    .panel-heading {
      padding: 0 20px !important;
      margin-bottom: 4px;
    }

    .panel-title {
      font-size: 18px !important;
    }

    /* Button responsive */
    .panel-default > .btn {
      width: calc(100% - 40px) !important;
      font-size: 17px !important;
      padding: 14px 20px !important;
      margin: 16px 20px 8px 20px !important;
    }

    /* Current balance */
    .current-balance {
      padding: 0 20px;
      margin-top: 14px !important;
      margin-bottom: 8px;
    }

    .current-balance .msg {
      font-size: 16px !important;
    }

    .amountBalance {
      font-size: 18px !important;
    }

    /* Card styles */
    .notes-cards-mobile {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .note-card-mobile {
      background-color: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }

    .note-card-mobile-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 18px;
      background-color: #fafbfc;
      border-bottom: 1px solid #e8e8e8;
    }

    .note-card-mobile-number {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .note-label {
      font-size: 13px;
      color: #888;
      text-transform: uppercase;
      font-weight: 500;
    }

    .note-number {
      font-size: 18px;
      font-weight: 600;
      color: #127285;
    }

    .note-status-badge {
      font-size: 13px;
      padding: 4px 10px;
      text-transform: capitalize;
    }

    .note-card-mobile-body {
      padding: 18px;
      display: flex;
      flex-direction: column;
      gap: 13px;
    }

    .note-card-mobile-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .highlight-row {
      padding: 12px;
      background-color: #f8fcfd;
      border-radius: 6px;
      margin: -6px -6px 6px -6px;
      padding: 12px;
    }

    .note-card-mobile-label {
      font-size: 14px;
      color: #888;
      flex-shrink: 0;
      min-width: 120px;
    }

    .note-card-mobile-value {
      font-size: 15px;
      color: #333;
      font-weight: 500;
      text-align: right;
      word-break: break-word;
    }

    .amount-value {
      font-size: 16px;
      color: #127285;
      font-weight: 600;
    }

    .note-card-mobile-action {
      padding: 16px 18px;
      background-color: #fafbfc;
      border-top: 1px solid #e8e8e8;
    }

    .btn-mobile-details {
      width: 100%;
      font-size: 16px;
      padding: 12px 16px;
      border-radius: 6px;
      font-weight: 500;
      background-color: #127285;
      color: #fff;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      transition: all 0.2s;
    }

    .btn-mobile-details i {
      font-size: 16px;
    }

    .btn-mobile-details:hover:not(:disabled) {
      background-color: #0d5a68;
    }

    .btn-mobile-details:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @media (max-width: 480px) {
    .mobile-card-view {
      padding: 0 16px;
    }

    .content-heading {
      font-size: 20px !important;
      padding: 18px 16px 14px 16px !important;
    }

    .panel-heading {
      padding: 0 16px !important;
    }

    .panel-title {
      font-size: 17px !important;
    }

    .panel-default > .btn {
      width: calc(100% - 32px) !important;
      font-size: 16px !important;
      padding: 13px 18px !important;
      margin: 14px 16px 8px 16px !important;
    }

    .current-balance {
      padding: 0 16px;
    }

    .current-balance .msg {
      font-size: 15px !important;
    }

    .amountBalance {
      font-size: 17px !important;
    }

    .notes-cards-mobile {
      gap: 14px;
    }

    .note-card-mobile-header {
      padding: 14px 16px;
    }

    .note-number {
      font-size: 17px;
    }

    .note-status-badge {
      font-size: 12px;
      padding: 3px 8px;
    }

    .note-card-mobile-body {
      padding: 16px;
      gap: 12px;
    }

    .note-card-mobile-label {
      font-size: 13px;
      min-width: 110px;
    }

    .note-card-mobile-value {
      font-size: 14px;
    }

    .amount-value {
      font-size: 15px;
    }

    .note-card-mobile-action {
      padding: 14px 16px;
    }

    .btn-mobile-details {
      font-size: 15px;
      padding: 11px 14px;
    }

    .btn-mobile-details i {
      font-size: 15px;
    }
  }

  @media (max-width: 360px) {
    .mobile-card-view {
      padding: 0 14px;
    }

    .content-heading {
      font-size: 18px !important;
      padding: 16px 14px 12px 14px !important;
    }

    .panel-heading {
      padding: 0 14px !important;
    }

    .panel-title {
      font-size: 16px !important;
    }

    .panel-default > .btn {
      width: calc(100% - 28px) !important;
      font-size: 15px !important;
      padding: 12px 16px !important;
      margin: 12px 14px 8px 14px !important;
    }

    .current-balance {
      padding: 0 14px;
    }

    .current-balance .msg {
      font-size: 14px !important;
    }

    .amountBalance {
      font-size: 16px !important;
    }

    .note-card-mobile-header {
      padding: 12px 14px;
    }

    .note-label {
      font-size: 12px;
    }

    .note-number {
      font-size: 16px;
    }

    .note-card-mobile-body {
      padding: 14px;
    }

    .note-card-mobile-label {
      font-size: 12px;
      min-width: 100px;
    }

    .note-card-mobile-value {
      font-size: 13px;
    }

    .amount-value {
      font-size: 14px;
    }

    .note-card-mobile-action {
      padding: 12px 14px;
    }

    .btn-mobile-details {
      font-size: 14px;
      padding: 10px 12px;
    }

    .btn-mobile-details i {
      font-size: 14px;
    }
  }

</style>



// WEBPACK FOOTER //
// NotesList.vue?1d81ccb1